import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Building2, Plus, Pencil, Trash2, ChevronRight, Layers } from 'lucide-react';
import { toast } from 'sonner';
import { ConfirmDeleteDialog } from '../components/ConfirmDeleteDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Organizacion = () => {
  const [gerencias, setGerencias] = useState([]);
  const [superintendencias, setSuperintendencias] = useState([]);
  const [selectedGerencia, setSelectedGerencia] = useState(null);
  const [loading, setLoading] = useState(true);

  // Dialog states
  const [isGerenciaDialogOpen, setIsGerenciaDialogOpen] = useState(false);
  const [editingGerencia, setEditingGerencia] = useState(null);
  const [gerenciaForm, setGerenciaForm] = useState({ nombre: '', descripcion: '' });

  const [isSupDialogOpen, setIsSupDialogOpen] = useState(false);
  const [editingSup, setEditingSup] = useState(null);
  const [supForm, setSupForm] = useState({ nombre: '', descripcion: '', gerencia_id: '' });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, itemId: null, tabla: '', nombre: '', deps: [], loadingDeps: false });

  const fetchGerencias = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/gerencias`);
      setGerencias(res.data);
    } catch {
      toast.error('Error al cargar gerencias');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSuperintendencias = useCallback(async (gerenciaId) => {
    try {
      const res = await axios.get(`${API}/superintendencias/gerencia/${gerenciaId}`);
      setSuperintendencias(res.data);
    } catch {
      toast.error('Error al cargar superintendencias');
    }
  }, []);

  useEffect(() => {
    fetchGerencias();
  }, [fetchGerencias]);

  useEffect(() => {
    if (selectedGerencia) {
      fetchSuperintendencias(selectedGerencia.id);
    } else {
      setSuperintendencias([]);
    }
  }, [selectedGerencia, fetchSuperintendencias]);

  // ========== Gerencia CRUD ==========
  const openCreateGerencia = () => {
    setEditingGerencia(null);
    setGerenciaForm({ nombre: '', descripcion: '' });
    setIsGerenciaDialogOpen(true);
  };

  const openEditGerencia = (g) => {
    setEditingGerencia(g);
    setGerenciaForm({ nombre: g.nombre, descripcion: g.descripcion || '' });
    setIsGerenciaDialogOpen(true);
  };

  const handleSaveGerencia = async (e) => {
    e.preventDefault();
    try {
      if (editingGerencia) {
        await axios.put(`${API}/gerencias/${editingGerencia.id}`, gerenciaForm);
        toast.success('Gerencia actualizada');
        if (selectedGerencia?.id === editingGerencia.id) {
          setSelectedGerencia({ ...selectedGerencia, ...gerenciaForm });
        }
      } else {
        await axios.post(`${API}/gerencias`, gerenciaForm);
        toast.success('Gerencia creada');
      }
      setIsGerenciaDialogOpen(false);
      fetchGerencias();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDeleteGerencia = async (g) => {
    setDeleteDialog({ open: true, itemId: g.id, tabla: 'gerencias', nombre: g.nombre, deps: [], loadingDeps: true });
    try {
      const res = await axios.get(`${API}/dependencias/gerencias/${g.id}`);
      setDeleteDialog(prev => ({ ...prev, deps: res.data.dependencias, loadingDeps: false }));
    } catch { setDeleteDialog(prev => ({ ...prev, loadingDeps: false })); }
  };

  // ========== Superintendencia CRUD ==========
  const openCreateSup = () => {
    if (!selectedGerencia) {
      toast.warning('Seleccione una gerencia primero');
      return;
    }
    setEditingSup(null);
    setSupForm({ nombre: '', descripcion: '', gerencia_id: selectedGerencia.id });
    setIsSupDialogOpen(true);
  };

  const openEditSup = (s) => {
    setEditingSup(s);
    setSupForm({ nombre: s.nombre, descripcion: s.descripcion || '', gerencia_id: s.gerencia_id });
    setIsSupDialogOpen(true);
  };

  const handleSaveSup = async (e) => {
    e.preventDefault();
    try {
      if (editingSup) {
        await axios.put(`${API}/superintendencias/${editingSup.id}`, supForm);
        toast.success('Superintendencia actualizada');
      } else {
        await axios.post(`${API}/superintendencias`, supForm);
        toast.success('Superintendencia creada');
      }
      setIsSupDialogOpen(false);
      if (selectedGerencia) fetchSuperintendencias(selectedGerencia.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDeleteSup = async (s) => {
    setDeleteDialog({ open: true, itemId: s.id, tabla: 'superintendencias', nombre: s.nombre, deps: [], loadingDeps: true });
    try {
      const res = await axios.get(`${API}/dependencias/superintendencias/${s.id}`);
      setDeleteDialog(prev => ({ ...prev, deps: res.data.dependencias, loadingDeps: false }));
    } catch { setDeleteDialog(prev => ({ ...prev, loadingDeps: false })); }
  };

  const confirmDeleteItem = async () => {
    const { itemId, tabla } = deleteDialog;
    try {
      if (tabla === 'gerencias') {
        await axios.delete(`${API}/gerencias/${itemId}`);
        toast.success('Gerencia eliminada');
        if (selectedGerencia?.id === itemId) setSelectedGerencia(null);
        fetchGerencias();
      } else {
        await axios.delete(`${API}/superintendencias/${itemId}`);
        toast.success('Superintendencia eliminada');
        if (selectedGerencia) fetchSuperintendencias(selectedGerencia.id);
      }
      setDeleteDialog({ open: false, itemId: null, tabla: '', nombre: '', deps: [], loadingDeps: false });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
      setDeleteDialog({ open: false, itemId: null, tabla: '', nombre: '', deps: [], loadingDeps: false });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Estructura Organizacional</h1>
        <p className="text-slate-500">Gestione las gerencias y sus superintendencias</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Gerencias Panel */}
        <Card className="border border-slate-200 shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Building2 className="w-5 h-5 text-teal-500" />
                Gerencias
              </CardTitle>
              <Button
                size="sm"
                onClick={openCreateGerencia}
                className="bg-teal-500 hover:bg-teal-600"
                data-testid="create-gerencia-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Nueva
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {gerencias.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-8" data-testid="no-gerencias-msg">
                No hay gerencias registradas
              </p>
            ) : (
              <div className="space-y-1">
                {gerencias.map((g) => {
                  const isSelected = selectedGerencia?.id === g.id;
                  return (
                    <div
                      key={g.id}
                      data-testid={`gerencia-row-${g.id}`}
                      className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all border ${
                        isSelected
                          ? 'bg-teal-50 border-teal-200 ring-1 ring-teal-300'
                          : 'border-transparent hover:bg-slate-50'
                      }`}
                      onClick={() => setSelectedGerencia(isSelected ? null : g)}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <ChevronRight
                          className={`w-4 h-4 text-slate-400 transition-transform flex-shrink-0 ${
                            isSelected ? 'rotate-90 text-teal-500' : ''
                          }`}
                        />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-800 truncate">{g.nombre}</p>
                          {g.descripcion && (
                            <p className="text-xs text-slate-400 truncate">{g.descripcion}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={(e) => { e.stopPropagation(); openEditGerencia(g); }}
                          data-testid={`edit-gerencia-${g.id}`}
                        >
                          <Pencil className="w-3.5 h-3.5 text-slate-500" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={(e) => { e.stopPropagation(); handleDeleteGerencia(g); }}
                          data-testid={`delete-gerencia-${g.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Superintendencias Panel */}
        <Card className="border border-slate-200 shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Layers className="w-5 h-5 text-teal-500" />
                Superintendencias
                {selectedGerencia && (
                  <span className="text-sm font-normal text-slate-400">
                    de {selectedGerencia.nombre}
                  </span>
                )}
              </CardTitle>
              <Button
                size="sm"
                onClick={openCreateSup}
                className="bg-teal-500 hover:bg-teal-600"
                disabled={!selectedGerencia}
                data-testid="create-superintendencia-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Nueva
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {!selectedGerencia ? (
              <div className="text-center py-12">
                <Building2 className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-500" data-testid="select-gerencia-msg">
                  Seleccione una gerencia para ver sus superintendencias
                </p>
              </div>
            ) : superintendencias.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-8" data-testid="no-superintendencias-msg">
                No hay superintendencias en esta gerencia
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Descripcion</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {superintendencias.map((s) => (
                    <TableRow key={s.id} data-testid={`sup-row-${s.id}`}>
                      <TableCell className="font-medium">{s.nombre}</TableCell>
                      <TableCell className="text-sm text-slate-500">{s.descripcion || '-'}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => openEditSup(s)}
                            data-testid={`edit-sup-${s.id}`}
                          >
                            <Pencil className="w-3.5 h-3.5 text-slate-500" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => handleDeleteSup(s)}
                            data-testid={`delete-sup-${s.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Gerencia Dialog */}
      <Dialog open={isGerenciaDialogOpen} onOpenChange={setIsGerenciaDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingGerencia ? 'Editar Gerencia' : 'Nueva Gerencia'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveGerencia} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="g-nombre">Nombre</Label>
              <Input
                id="g-nombre"
                value={gerenciaForm.nombre}
                onChange={(e) => setGerenciaForm({ ...gerenciaForm, nombre: e.target.value })}
                required
                placeholder="Ej: Gerencia de Operaciones"
                data-testid="gerencia-nombre-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="g-desc">Descripcion (opcional)</Label>
              <Input
                id="g-desc"
                value={gerenciaForm.descripcion}
                onChange={(e) => setGerenciaForm({ ...gerenciaForm, descripcion: e.target.value })}
                placeholder="Descripcion breve"
                data-testid="gerencia-desc-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsGerenciaDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-teal-500 hover:bg-teal-600" data-testid="save-gerencia-btn">
                {editingGerencia ? 'Actualizar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Superintendencia Dialog */}
      <Dialog open={isSupDialogOpen} onOpenChange={setIsSupDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingSup ? 'Editar Superintendencia' : 'Nueva Superintendencia'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveSup} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="s-gerencia">Gerencia</Label>
              <Select
                value={supForm.gerencia_id}
                onValueChange={(val) => setSupForm({ ...supForm, gerencia_id: val })}
              >
                <SelectTrigger data-testid="sup-gerencia-select">
                  <SelectValue placeholder="Seleccionar gerencia" />
                </SelectTrigger>
                <SelectContent>
                  {gerencias.map((g) => (
                    <SelectItem key={g.id} value={g.id}>{g.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="s-nombre">Nombre</Label>
              <Input
                id="s-nombre"
                value={supForm.nombre}
                onChange={(e) => setSupForm({ ...supForm, nombre: e.target.value })}
                required
                placeholder="Ej: Superintendencia Mina"
                data-testid="sup-nombre-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="s-desc">Descripcion (opcional)</Label>
              <Input
                id="s-desc"
                value={supForm.descripcion}
                onChange={(e) => setSupForm({ ...supForm, descripcion: e.target.value })}
                placeholder="Descripcion breve"
                data-testid="sup-desc-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsSupDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-teal-500 hover:bg-teal-600" data-testid="save-sup-btn">
                {editingSup ? 'Actualizar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        open={deleteDialog.open}
        onOpenChange={(v) => !v && setDeleteDialog({ open: false, itemId: null, tabla: '', nombre: '', deps: [], loadingDeps: false })}
        onConfirm={confirmDeleteItem}
        title={deleteDialog.tabla === 'gerencias' ? 'Eliminar Gerencia' : 'Eliminar Superintendencia'}
        description={`¿Esta seguro de eliminar "${deleteDialog.nombre}"? Esta accion no se puede deshacer.`}
        dependencies={deleteDialog.deps}
        loadingDeps={deleteDialog.loadingDeps}
      />
    </div>
  );
};